import { defineStore } from "pinia"
import { orderBy } from "lodash"
import { DateTime } from "luxon"
import type { User } from "@/types/models/user"
import type { Notification } from "@/types/models/notification"
import http from "@/http"

export interface State {
  token: string | null
  user: User | null
  notifications: Notification[]
}

interface LoginResponse {
  token: string
  user: User
  notifications: Notification[]
}

interface SyncResponse {
  user: User
  notifications: Notification[]
}

const load = (): State => {
  const token = localStorage.getItem("jwt")
  const json = localStorage.getItem("user")

  return {
    token,
    user: json ? JSON.parse(json) as User : null,
    notifications: []
  }
}

const persist = ({ token, user }: Partial<State>) => {
  if(token) {
    localStorage.setItem("jwt", token)
  }

  if(user) {
    localStorage.setItem("user", JSON.stringify(user))
  }
}

export const useUserStore = defineStore("user", {
  state: () => load(),
  getters: {
    authenticated: (state) => !!state.token && !!state.user,
    sortedNotifications: (state): Notification[] => orderBy(
      state.notifications,
      n => [n.status ? 0 : 1, DateTime.fromISO(n.creationDate, { zone: "UTC" }).toMillis()],
      "desc"
    ),
    unreadNotifications(): Notification[] {
      return this.sortedNotifications.filter(n => !n.status)
    },
    readNotifications(): Notification[] {
      return this.sortedNotifications.filter(n => n.status)
    },
    hasUnreadNotification(): boolean {
      return this.unreadNotifications.length > 0
    },
  },
  actions: {
    async register(data: Record<string, unknown>) {
      const { data: response }: { data: LoginResponse } =
        await http.post("/auth/register", data)
      
      this.appLogin(response)
    },
    async login(credentials: Record<string, unknown>) {
      const { data: response }: { data: LoginResponse } =
        await http.post("/auth/login", credentials)

      this.appLogin(response)
    },
    async validate(data: Record<string, unknown>) {
      const { data: response }: { data: LoginResponse } =
        await http.post("/auth/validateemail", data)
      
      this.appLogin(response)
    },
    async triggerValidation(data: Record<string, unknown>) {
      const { data: date }: { data: string } =
        await http.post("/auth/sendvalidationemail", data)

      if(!this.user) return

      this.user.validationIntentDate = date

      persist({ user: this.user })
    },
    async reset(data: Record<string, unknown>) {
      const { data: response }: { data: LoginResponse } =
        await http.post("auth/recover/validatepassword", data)
      
      this.appLogin(response)
    },
    async update(data: Record<string, unknown>) {
      const { data: user }: { data: User } =
        await http.put("users", data)

      this.appUpdate(user)
    },
    async sync() {
      const { data: { user, notifications } }: { data: SyncResponse } =
        await http.get("users")

      this.appUpdate(user)
      this.notifications = notifications
    },
    appLogin({ token, user, notifications }: LoginResponse) {
      this.$patch({ token, user, notifications })
      persist({ token, user })
    },
    appLogout() {
      this.$patch({ token: null, user: null })

      localStorage.removeItem("jwt")
      localStorage.removeItem("user")
    },
    appUpdate(user: Partial<User>) {
      this.user = Object.assign({}, this.user, user)

      persist({ user: this.user })
    },
    updateNotificationRead(id: number, status: boolean) {
      const match = this.notifications.find(n => n.userNotificationId === id)

      if(!match) return

      match.status = status
    },
    deleteNotification(id: number) {
      this.notifications = this.notifications.filter(n => n.userNotificationId !== id)
    }
  }
})
